import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppContext } from "../utils/context/app-context";
import { TextArea } from "../components/FormComponents";
import {
	Button,
	ContentContainer,
	TitleHeader,
	TableLoading,
	AlertMessage,
} from "../components/UiComponents";
import {
	CourseHeader,
	CourseWrapper,
} from "../components/CourseComponents";
import useDocumentTitle from "../components/useDocumentTitle";
import { useUserIsReadOnly } from "../components/userFunctions";


const HealthQuestionnairePage = () => {
	const { state, dispatch, api } = useAppContext();
	const isOffline = state.offline;
	useDocumentTitle("Health Questionnaire");
	const { questionnaireId } = useParams();
	const [questionnaire, setQuestionnaire] = useState({});
	const [loading, setLoading] = useState(true);
	const [updating, setUpdating] = useState(false);
	const [updated, setUpdated] = useState(false);
	const [invalid, setInvalid] = useState(false);
	let course = api.getCurrentCourse();
	const isReadOnly = useUserIsReadOnly();
	console.log(isReadOnly)

	useEffect(() => {
		fetchData();
	}, []);

	useEffect(() => {
		if (updated) {
			setTimeout(() => {
				setUpdated(false);
			}, 2000);
		}
	}, [updated]);

	function prepopulateComments(form) {
		if (!form.has_been_checked && (!form.comments || form.comments.trim() === "")) {
			let hasFlag = false;
			if (form.answers) {
				const healthQuestions = [
					"heart_condition", "pacemaker_fitted", "high_blood_pressure", "neurological_disease", "dizzy_spells",
					"cold_chest_infection", "back_trouble", "whiplash", "arthritis", "broken_bones", "diabetes", "asthma",
					"blackouts", "anaemic", "medication", "operations", "prosthesis", "hearing_difficulties",
					"health_concerns", "pregnant", "reasons_for_not_participating"
				];
				for (const key of healthQuestions) {
					const ans = form.answers[key];
					if (ans) {
						if (ans.flag_answer === '1' || ans.flag_answer === 1 || (typeof ans.answer === 'string' && ans.answer.toLowerCase() === 'yes')) {
							hasFlag = true;
							break;
						}
					}
				}
			}
			if (!hasFlag) {
				form.comments = "Participant has reported no health concerns (answered No to all questions)";
			} else {
				form.comments = "";
			}
		}
		return form;
	}

	async function fetchData() {
		if (isOffline) {
			const questionnaire = api.findHealthQuestionnaireById(questionnaireId);
			if (questionnaire?.form) {
				const form = prepopulateComments({ ...questionnaire.form });
				dispatch({
					type: "SET_STUDENT_HEALTH_QUESTIONNAIRE",
					payload: {
						form: form,
						course_id: course.id,
						date_id: state.current_date_id,
						student_id: form.student_id,
					},
				});
				setQuestionnaire(form);
				setLoading(false);
				return;
			}
		}

		// eslint-disable-next-line no-unused-vars
		const { data, error } = await api.fetchData(
			`/health-questionnaire/${questionnaireId}`
		);
		// console.log(data.form);
		if (data?.form) {
			const form = prepopulateComments({ ...data.form });
			dispatch({
				type: "SET_STUDENT_HEALTH_QUESTIONNAIRE",
				payload: {
					form: form,
					course_id: course.id,
					date_id: state.current_date_id,
					student_id: form.student_id,
				},
			});
			setQuestionnaire(form);
			setLoading(false);
		} else {
			setLoading(false);
		}
	}

	function updateComments(val) {
		if (isReadOnly) {
			return;
		}
		setQuestionnaire({
			...questionnaire,
			comments: val,
		});
	}

	async function save(e) {
		e.preventDefault();
		setInvalid(false);

		if (questionnaire.comments === "") {
			setInvalid(true);
			return;
		}

		setUpdating(true);
		setUpdated(false);
		const { data } = await api.fetchData(
			`/health-questionnaire/${questionnaireId}`,
			"POST",
			{ comments: questionnaire.comments }
		);

		console.log(data);
		if (data?.updated) {
			setUpdating(false);
			setUpdated(true);
			// Make copy of questionnaire and update has_been_checked to true on questionnaire
			let updatedQuestionnaire = { ...questionnaire };
			updatedQuestionnaire.has_been_checked = true;
			setQuestionnaire(updatedQuestionnaire);
			dispatch({
				type: "SET_STUDENT_HEALTH_QUESTIONNAIRE",
				payload: {
					form: updatedQuestionnaire,
					course_id: course.id,
					date_id: state.current_date_id,
					student_id: updatedQuestionnaire.student_id,
				},
			});
		}
	}

	function updateButtonText() {
		if (updating) {
			return "Updating...";
		} else if (updated) {
			return "Updated";
		} else if (questionnaire.has_been_checked) {
			return "Update Comments";
		} else {
			return "Check and Approve";
		}
	}

	return (
		<div className="health-questionnaire-page">
			<CourseHeader />
			<ContentContainer classes="mb-4">
				<CourseWrapper>
					<TitleHeader title="Health Questionnaire" />
					<div className="row">
						<div className="col-md-8">
							{loading ? (
								<TableLoading />
							) : (
								<>
									{questionnaire.has_been_checked && (
										<div className="mb-4">
											<AlertMessage type="success" message="Questionnaire has been checked" />
										</div>
									)}
									<Questionnaire questionnaire={questionnaire} />
								</>
							)}
						</div>
						<div className="col-md-4">
							<div className="health-questionnaire-sidebar">
								{loading ? (
									<TableLoading />
								) : (
									<>
										<div className="health-questionnaire-sidebar__header">
											<h3 className="health-questionnaire-sidebar__title">
												{questionnaire.student_first_name}{" "}
												{questionnaire.student_last_name}
											</h3>
											<h4 className="health-questionnaire-sidebar__subtitle">
												{questionnaire.student_email}
											</h4>
										</div>
										<div className="health-questionnaire-sidebar__content">
											<form action="" onSubmit={save}>
												<TextArea
													name="comments"
													value={questionnaire.comments ?? ""}
													onChange={(e) =>
														updateComments(e.target.value)
													}
													rows={8}
													placeholder="Add comments here....."
													required={true}
												/>
												<p className="health-questionnaire-sidebar__text">
													By clicking the button below you are
													confirming you as the tutor have check
													all the details of this health form.
												</p>
												{invalid && (
													<div className="mb-2">
														<AlertMessage type="danger" message="Please enter comments" />
													</div>
												)}
												<Button
													text={updateButtonText()}
													type="primary"
													buttonType="submit"
													disabled={updating || isReadOnly || isOffline}
													className={
														"health-questionnaire-sidebar__button"
													}
												/>
											</form>
										</div>
									</>
								)}
							</div>
						</div>
					</div>
				</CourseWrapper>
			</ContentContainer>
		</div>
	);
};

const Questionnaire = ({ questionnaire }) => {
	function getAnswer(answer) {
		if (questionnaire.answers[answer]) {
			return questionnaire.answers[answer].answer;
		}

		return "-";
	}

	function isFlag(answer) {
		if (questionnaire.answers[answer]) {
			// console.log(questionnaire.answers[answer].flag_answer)
			if (questionnaire.answers[answer].flag_answer === '1') return true;
			if (typeof questionnaire.answers[answer].answer === 'string' && questionnaire.answers[answer].answer.toLowerCase() === 'yes') return true;
		}

		return false;
	}

	function parseDate(answer) {
		// Parse date from YYY-mm-dd to dd/mm/YYYY
		let date = getAnswer(answer);
		if (date !== "-") {
			let parts = date.split("-");
			return `${parts[2]}/${parts[1]}/${parts[0]}`;
		}
	}

	return (
		<div className="view-form">
			<Question
				label="Have you a history or do you have a heart condition?"
				answer={getAnswer("heart_condition")}
				uppercaseFirst={true}
			/>
			{isFlag("heart_condition") && (
				<Question
					label="Please note area of concern"
					answer={getAnswer("heart_condition_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Do you have a pacemaker fitted?"
				answer={getAnswer("pacemaker_fitted")}
				uppercaseFirst={true}
			/>
			{isFlag("pacemaker_fitted") && (
				<Question
					label="If yes, please explain"
					answer={getAnswer("pacemaker_fitted_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Have you a history of or do you have high blood pressure?"
				answer={getAnswer("high_blood_pressure")}
				uppercaseFirst={true}
			/>
			{isFlag("high_blood_pressure") && (
				<Question
					label="If yes, please explain. Include any medication management and how this influences your daily life"
					answer={getAnswer("high_blood_pressure_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Have you ever had, or do you currently suffer with Chronic Neurological Disease (e.g., Parkinson’s Disease or Multiple Sclerosis)?"
				answer={getAnswer("neurological_disease")}
				uppercaseFirst={true}
			/>
			{isFlag("neurological_disease") && (
				<Question
					label="If yes, please explain. Include any medication management and how this influences your daily life"
					answer={getAnswer("neurological_disease_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Have you a history of or do you currently suffer with dizzy spells or balance problems?"
				answer={getAnswer("dizzy_spells")}
				uppercaseFirst={true}
			/>
			{isFlag("dizzy_spells") && (
				<Question
					label="If yes, please explain. Include any medication management and how this influences your daily life"
					answer={getAnswer("dizzy_spells_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Do you currently have a heavy cold, or do you have a chest infection?"
				answer={getAnswer("cold_chest_infection")}
				uppercaseFirst={true}
			/>
			{isFlag("cold_chest_infection") && (
				<Question
					label="If yes, please explain"
					answer={getAnswer("cold_chest_infection_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Have you a history of, or do you have any, back trouble/pain?"
				answer={getAnswer("back_trouble")}
				uppercaseFirst={true}
			/>
			{isFlag("back_trouble") && (
				<Question
					label="If yes, please explain. Include any current or previous management (treatment, therapy or medication), and how this influences your daily life"
					answer={getAnswer("back_trouble_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Have you suffered from any whiplash injuries?"
				answer={getAnswer("whiplash")}
				uppercaseFirst={true}
			/>
			{isFlag("whiplash") && (
				<Question
					label="If yes, please explain. Include any medication management and how this influences your daily life"
					answer={getAnswer("whiplash_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Do you have arthritis or joint pain of any sort?"
				answer={getAnswer("arthritis")}
				uppercaseFirst={true}
			/>
			{isFlag("arthritis") && (
				<Question
					label="If yes, please explain. Include any current or previous management (treatment, therapy or medication), and how this influences your daily life"
					answer={getAnswer("arthritis_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Have you broken any bones in the last year or previously that you feel may affect your ability to take part? NB: a fracture and a break are the same thing!"
				answer={getAnswer("broken_bones")}
				uppercaseFirst={true}
			/>
			{isFlag("broken_bones") && (
				<Question
					label="If yes, please explain. Comment on the area, method of injury (i.e. was this due to trauma or osteoporosis?), and any residual discomfort or limitation of movement which currently exists as a result of the injury"
					answer={getAnswer("broken_bones_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Do you have diabetes?"
				answer={getAnswer("diabetes")}
				uppercaseFirst={true}
			/>
			{isFlag("diabetes") && (
				<Question
					label="If yes, please explain. Include any medication, management and how this influences your daily life"
					answer={getAnswer("diabetes_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Do you have asthma or any respiratory limitation?"
				answer={getAnswer("asthma")}
				uppercaseFirst={true}
			/>
			{isFlag("asthma") && (
				<Question
					label="If yes, please explain. Include any medication, management and how this influences your daily life"
					answer={getAnswer("asthma_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Have you ever had, or do you currently suffer with blackouts, fits, epilepsy, or fainting?"
				answer={getAnswer("blackouts")}
				uppercaseFirst={true}
			/>
			{isFlag("blackouts") && (
				<Question
					label="If yes, please explain. Include any medication, management, frequency and type of seizures and how this influences your daily life"
					answer={getAnswer("blackouts_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Are you anaemic?"
				answer={getAnswer("anaemic")}
				uppercaseFirst={true}
			/>
			{isFlag("anaemic") && (
				<Question
					label="If yes, please explain. Include any medication, management and how this influences your daily life"
					answer={getAnswer("anaemic_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Are you, at present, on any form of medication that you feel may affect your ability to take part in the course?"
				answer={getAnswer("medication")}
				uppercaseFirst={true}
			/>
			{isFlag("medication") && (
				<Question
					label="If yes, please explain. Include any medication, management and how this influences your daily life"
					answer={getAnswer("medication_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Have you had any operations within the last 6 months that you feel may affect your ability to take part in the course?"
				answer={getAnswer("operations")}
				uppercaseFirst={true}
			/>
			{isFlag("operations") && (
				<Question
					label="If so, please explain"
					answer={getAnswer("operations_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Do you have a prosthesis, joint replacement, or implant?"
				answer={getAnswer("prosthesis")}
				uppercaseFirst={true}
			/>
			{isFlag("prosthesis") && (
				<Question
					label="If so, please explain"
					answer={getAnswer("prosthesis_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Have you ever had, or do you currently suffer with hearing difficulties, eye disease or significant eyesight problems?"
				answer={getAnswer("hearing_difficulties")}
				uppercaseFirst={true}
			/>
			{isFlag("hearing_difficulties") && (
				<Question
					label="If so, please explain"
					answer={getAnswer("hearing_difficulties_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Have you have seen a doctor, or other health care professional, in the last 3 months with any concern that may affect your ability to take part in the course?"
				answer={getAnswer("health_concerns")}
				uppercaseFirst={true}
			/>
			{isFlag("health_concerns") && (
				<Question
					label="If so, please explain"
					answer={getAnswer("health_concerns_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Are you, or do you think you might be, pregnant?"
				answer={getAnswer("pregnant")}
				uppercaseFirst={true}
			/>
			{isFlag("pregnant") && (
				<Question
					label="If so, please explain"
					answer={getAnswer("pregnant_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Is there any other reason, physical, emotional, or psychological a symptom, condition or limitation that may restrict or prevent you from safely taking part or carrying out Physical Intervention / Restraint or Breakaway physical skills / procedures?"
				answer={getAnswer("reasons_for_not_participating")}
				uppercaseFirst={true}
			/>
			{isFlag("reasons_for_not_participating") && (
				<Question
					label="If so, please explain"
					answer={getAnswer("reasons_for_not_participating_notes")}
					flagAnswer={true}
				/>
			)}
			<Question
				label="Date Of Birth"
				answer={parseDate("dob")}
				uppercaseFirst={true}
			/>
			<Question
				label="Contact Number"
				answer={getAnswer("contact_number")}
				uppercaseFirst={true}
			/>
		</div>
	);
};

export const Question = ({
	label,
	answer,
	uppercaseFirst = false,
	flagAnswer = false,
	className = "",
	multilineAnswer = false,
}) => {
	let classes = ["view-form__form-answer"];

	let isYesAnswer = false;
	if (typeof answer === 'string' && answer.toLowerCase() === 'yes') {
		isYesAnswer = true;
	}

	if (flagAnswer || isYesAnswer) {
		classes.push("view-form__form-answer--flag");
	}

	if (className) {
		classes.push(className);
	}

	if (multilineAnswer) {
		classes.push("view-form__form-answer--multiline");
	}

	return (
		<div className={classes.join(" ")}>
			<div className="view-form__question">{label}</div>
			<div
				className={`view-form__answer ${uppercaseFirst ? "view-form__answer--uppercase-first" : ""
					}`}>
				{answer}
			</div>
		</div>
	);
};

export default HealthQuestionnairePage;
